import { Controller } from "@hotwired/stimulus"

/**
 * Show More Controller - Collapse/Expand list items
 *
 * Shows initial N items with toggle button to expand/collapse.
 * Uses CSS classes for visibility (no inline styles pollution).
 *
 * Usage:
 * <div data-controller="show-more"
 *      data-show-more-initial-count-value="4">
 *   <div data-show-more-target="item">...</div>
 *   <button data-show-more-target="toggle" data-action="show-more#toggle">
 *     <span data-show-more-target="text">顯示更多</span>
 *     <svg data-show-more-target="icon">...</svg>
 *   </button>
 * </div>
 */
export default class extends Controller {
  static targets = ["item", "toggle", "text", "icon"]
  static values = {
    initialCount: { type: Number, default: 4 },
    expanded: { type: Boolean, default: false },
    showMoreText: { type: String, default: "顯示更多" },
    showLessText: { type: String, default: "收起" }
  }

  connect() {
    this.updateVisibility()
  }

  toggle() {
    this.expandedValue = !this.expandedValue
    this.updateVisibility()
  }

  updateVisibility() {
    const initialCount = this.initialCountValue

    this.itemTargets.forEach((item, index) => {
      const shouldShow = this.expandedValue || index < initialCount

      if (shouldShow) {
        item.classList.remove("hidden")
        // Staggered fade-in animation for newly visible items
        if (this.expandedValue && index >= initialCount) {
          this.animateIn(item, index - initialCount)
        }
      } else {
        item.classList.add("hidden")
      }
    })

    this.updateToggleUI()
  }

  /**
   * Animate item appearance with staggered delay
   * @param {HTMLElement} item - Item to animate
   * @param {number} offset - Position offset for stagger calculation
   */
  animateIn(item, offset) {
    item.style.opacity = "0"
    item.style.transform = "translateY(-8px)"

    requestAnimationFrame(() => {
      item.style.transition = "opacity 0.15s ease-out, transform 0.15s ease-out"
      item.style.transitionDelay = `${offset * 30}ms`
      item.style.opacity = "1"
      item.style.transform = "translateY(0)"
    })
  }

  updateToggleUI() {
    // Update button text
    if (this.hasTextTarget) {
      this.textTarget.textContent = this.expandedValue
        ? this.showLessTextValue
        : this.showMoreTextValue
    }

    // Update icon rotation
    if (this.hasIconTarget) {
      this.iconTarget.style.transform = this.expandedValue ? "rotate(180deg)" : "rotate(0)"
    }
  }
}
